import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
  memo,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import anime from "animejs/lib/anime.es.js";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { POPUP_OVERLAY_CONFIG } from "../../../pixiSettings";
import { SOUND_NAME } from "../../../sounds";
import { soundManager } from "../../../SoundManager";
import { safeInit } from "../../../utilities";
import { selectShowHoldAndWinIntro } from "../../../State/selectors";
import {
  selectHoldAndWinRespinsLeft,
  selectShowHoldAndWinReload,
} from "../../../State/Slices/holdAndWinSlice";
import HoldAndWinIntroController from "./HoldAndWinIntroController";
import FeatureIntroController from "../FeatureIntro/FeatureIntroController";

const OVERLAY_BACKGROUND_COLOR = POPUP_OVERLAY_CONFIG.COLOR;
const OVERLAY_BACKGROUND_ALPHA = POPUP_OVERLAY_CONFIG.ALPHA;
const OVERLAY_FADE_DURATION = POPUP_OVERLAY_CONFIG.FADE_DURATION;

/**
 * HoldAndWinIntroDialog — the Cleopatra's Respins intro popup.
 *
 * A copy of FreeSpinIntroDialog (same backdrop, tap, spacebar and sequence),
 * opened by useWinController once the trigger coins' line has played, and
 * handing over to the wipe when it closes.
 *
 * variant "reload" — the mid-feature reload popup (YOU HAVE / n / RESPIN(S)
 * LEFT), raised by HoldAndWinControlContext. Same component, mounted twice
 * from Dialogs.jsx, as the FS intro / reload pair.
 */
const HoldAndWinIntroDialog = ({ variant = "intro" }) => {
  const isReload = variant === "reload";
  // The intro card dims deeper than the reload popup.
  const overlayTargetAlpha = isReload
    ? OVERLAY_BACKGROUND_ALPHA
    : POPUP_OVERLAY_CONFIG.FEATURE_INTRO_ALPHA;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const showIntroPopup = useSelector(selectShowHoldAndWinIntro);
  const showReloadPopup = useSelector(selectShowHoldAndWinReload);
  const showIntro = isReload ? showReloadPopup : showIntroPopup;
  const respinsLeft = useSelector(selectHoldAndWinRespinsLeft);

  const introControllerRef = useRef(null);
  const introContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const dimensionsContext = useDimensions();
  const { dimensions, uiSymbolHeight: uiSymbolSize, device } = dimensionsContext;

  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const overlayAnimRef = useRef(null);

  useImperativeHandle(overlayRef, () => ({
    fadeInBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: overlayTargetAlpha,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
        complete: () => callback?.(),
      });
    },
    fadeOutBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: overlayTargetAlpha },
        alpha: 0,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
        complete: () => {
          setOverlayAlpha(0);
          callback?.();
        },
      });
    },
    setAlpha(alpha) {
      setOverlayAlpha(alpha);
    },
  }));

  // Controller, once.
  useEffect(() => {
    if (introContainerRef.current && !introControllerRef.current) {
      safeInit(() => {
        // introControllerRef.current = new HoldAndWinIntroController(
        // The INTRO is the feature_intro card now (2026-10-01); the reload
        // keeps the feature_spins popup.
        introControllerRef.current = isReload
          ? new HoldAndWinIntroController(
              introContainerRef.current,
              dimensionsContext,
              dispatch,
              uiSymbolSize,
              overlayRef,
              t,
              variant,
            )
          : new FeatureIntroController(
              introContainerRef.current,
              dimensionsContext,
              dispatch,
              uiSymbolSize,
              overlayRef,
              t,
              "hold_and_win",
            );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    introControllerRef.current?.updateDimensions(dimensionsContext, uiSymbolSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize, device]);

  // Play when raised.
  useEffect(() => {
    if (introControllerRef.current && showIntro) {
      introControllerRef.current.updateRespins(respinsLeft);
      introControllerRef.current.startSequence();
    }
  }, [showIntro, respinsLeft]);

  const handleBackgroundClick = useCallback(
    (evt) => {
      evt?.preventDefault?.();
      evt?.stopPropagation?.();
      evt?.stopImmediatePropagation?.();
      if (introControllerRef.current && showIntro) {
        if (introControllerRef.current.handleUserClick()) {
          soundManager.play(SOUND_NAME.tap_to_continue);
        }
      }
    },
    [showIntro],
  );

  // Spacebar, as the FS intro.
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }
      if (event.repeat) {
        event.preventDefault();
        event.stopPropagation?.();
        event.stopImmediatePropagation?.();
        return;
      }
      if (!showIntro || !introControllerRef.current) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      try {
        if (introControllerRef.current.handleUserClick()) {
          soundManager.play(SOUND_NAME.tap_to_continue);
        }
      } catch (error) {
        introControllerRef.current?.finishAnimation?.();
      }
    };

    if (showIntro) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false,
      });
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [showIntro]);

  useEffect(() => {
    return () => {
      introControllerRef.current?.cleanup();
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
    };
  }, []);

  const containerPosition = useMemo(
    () => ({ x: dimensions.width / 2, y: dimensions.height / 2 }),
    [dimensions.width, dimensions.height],
  );

  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_BACKGROUND_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  return (
    <Container
      name={isReload ? "hold_and_win_reload" : "hold_and_win_intro"}
      x={containerPosition.x}
      y={containerPosition.y}
      sortableChildren={true}
      zIndex={9}
      ref={introContainerRef}
      eventMode={showIntro ? "auto" : "none"}
    >
      <Graphics
        name="hold_and_win_intro_overlay"
        draw={drawOverlay}
        alpha={overlayAlpha}
        x={-containerPosition.x}
        y={-containerPosition.y}
        zIndex={0}
        eventMode={showIntro ? "static" : "none"}
        pointerdown={handleBackgroundClick}
        cursor="default"
      />
    </Container>
  );
};

export default memo(HoldAndWinIntroDialog);
